import React, { useEffect, useRef, useState } from "react"
import { Link, useParams, useSearchParams } from "react-router-dom"
import { api } from "../../lib/api"
import { trackPurchase } from "../../lib/analytics"
import { AppShell } from "../ui/AppShell"
import { Button } from "../ui/Button"
import { Card } from "../ui/Card"
import { Spinner } from "../ui/Spinner"
import type { PurchaseStatus } from "../../types"

type View = "polling" | "paid" | "failed" | "timeout" | "no_purchase"

const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS = 30000

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" })
}

export default function PlanoStatus() {
  const { status } = useParams<{ status: string }>()
  const [searchParams] = useSearchParams()
  const purchaseId = searchParams.get("purchase_id")

  const failedRoute = status === "failure"
  const [view, setView] = useState<View>(failedRoute ? "failed" : purchaseId ? "polling" : "no_purchase")
  const [purchase, setPurchase] = useState<PurchaseStatus | null>(null)
  const conversionFired = useRef(false)

  useEffect(() => {
    if (failedRoute || !purchaseId) return
    let stopped = false
    const startedAt = Date.now()

    async function poll() {
      if (stopped) return
      try {
        const p = await api.get<PurchaseStatus>(`/api/purchases/${purchaseId}`)
        if (stopped) return
        setPurchase(p)
        if (p.status === "paid") {
          if (!conversionFired.current) {
            conversionFired.current = true
            trackPurchase(p.amount_cents / 100, p.id)
          }
          setView("paid")
          return
        }
        if (p.status === "failed") {
          setView("failed")
          return
        }
      } catch {
        // erro transitório: continua tentando dentro da janela
      }
      if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
        setView("timeout")
        return
      }
      window.setTimeout(poll, POLL_INTERVAL_MS)
    }

    void poll()
    return () => {
      stopped = true
    }
  }, [failedRoute, purchaseId])

  return (
    <AppShell>
      <div className="mx-auto flex min-h-[55vh] max-w-sm flex-col items-center justify-center text-center">
        {view === "polling" && (
          <Card className="w-full py-10">
            <div className="flex flex-col items-center gap-4">
              <Spinner className="h-9 w-9" />
              <div>
                <h1 className="text-lg font-bold text-navy-900">Confirmando pagamento...</h1>
                <p className="mt-1 text-sm text-navy-500">
                  Aguardando confirmação do pagamento. Pix pode levar alguns instantes.
                </p>
              </div>
            </div>
          </Card>
        )}

        {view === "paid" && (
          <Card highlighted className="w-full py-10">
            <div className="flex flex-col items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gold-500/15">
                <svg viewBox="0 0 24 24" fill="none" className="h-9 w-9 text-gold-600" aria-hidden="true">
                  <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-navy-900">Pagamento confirmado!</h1>
                {purchase && (
                  <p className="mt-2 text-sm text-navy-700">
                    Plano {purchase.item_name}:{" "}
                    <span className="font-bold text-gold-600">
                      acesso liberado{purchase.access_expires_at ? ` até ${formatDate(purchase.access_expires_at)}` : ""}
                    </span>
                    .
                  </p>
                )}
                <p className="mt-1 text-sm text-navy-500">Bons estudos — a farda te espera.</p>
              </div>
              <Link to="/dashboard" className="w-full">
                <Button size="lg" block>
                  Ir para o painel
                </Button>
              </Link>
            </div>
          </Card>
        )}

        {view === "failed" && (
          <Card className="w-full py-10">
            <div className="flex flex-col items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500/15">
                <svg viewBox="0 0 24 24" fill="none" className="h-8 w-8 text-red-400" aria-hidden="true">
                  <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              </div>
              <div>
                <h1 className="text-lg font-bold text-navy-900">Pagamento não concluído</h1>
                <p className="mt-1 text-sm text-navy-500">
                  Nenhum valor foi cobrado. Você pode tentar de novo quando quiser.
                </p>
              </div>
              <Link to="/plano" className="w-full">
                <Button size="lg" block>
                  Tentar de novo
                </Button>
              </Link>
            </div>
          </Card>
        )}

        {view === "timeout" && (
          <Card className="w-full py-10">
            <div className="flex flex-col items-center gap-4">
              <Spinner className="h-8 w-8" />
              <div>
                <h1 className="text-lg font-bold text-navy-900">Ainda aguardando confirmação</h1>
                <p className="mt-1 text-sm text-navy-500">
                  Seu pagamento está sendo processado. Assim que for confirmado, o acesso é liberado no seu painel
                  automaticamente.
                </p>
              </div>
              <Link to="/dashboard" className="w-full">
                <Button variant="secondary" size="lg" block>
                  Ir para o painel
                </Button>
              </Link>
            </div>
          </Card>
        )}

        {view === "no_purchase" && (
          <Card className="w-full py-10">
            <div className="flex flex-col items-center gap-4">
              <p className="text-sm text-navy-500">Não encontramos a compra. Volte e tente de novo.</p>
              <Link to="/plano" className="w-full">
                <Button size="lg" block>
                  Ver planos
                </Button>
              </Link>
            </div>
          </Card>
        )}
      </div>
    </AppShell>
  )
}
